import { useState } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { clsx } from 'clsx'

import { useAuthStore } from '../../../shared/store/authStore'
import { useNotifications } from '../../../shared/hooks/useNotifications'
import {
  LayoutDashboard, Bell, Building2, Users, BookUser,
  ShoppingCart, BarChart2, DollarSign,
  Menu, X, LogOut,
  Cog,
  UserCog,
  Palette,
  Archive,
  Truck,
  Megaphone,
  Scissors,
  Layers,
  Package,
} from 'lucide-react'

const NAV_GROUPS = [
  {
    section: 'Overview',
    items: [
      { to: '/admin', icon: LayoutDashboard, label: 'Dashboard' },
      { to: '/admin/notifications', icon: Bell, label: 'Notifications' },
    ],
  },
  {
    section: 'Setup',
    items: [
      { to: '/admin/units', icon: Building2, label: 'Unit Management' },
      { to: '/admin/machines', icon: Cog, label: 'Machines & Positions' },
      { to: '/admin/operators', icon: UserCog, label: 'Operators' },
      { to: '/admin/users', icon: Users, label: 'User Management' },
      { to: '/admin/parties', icon: BookUser, label: 'Parties / Customers' },
      { to: '/admin/vendors', icon: BookUser, label: 'Vendors' },
      { to: '/admin/shades', icon: Palette, label: 'Shade Masters' },
      { to: '/admin/containers', icon: Archive, label: 'Containers' },
    ],
  },
  {
    section: 'Operations',
    items: [
      { to: '/admin/procurement', icon: ShoppingCart, label: 'Procurement' },
      { to: '/admin/cutting-logs', icon: Scissors, label: 'Cutting Records' },
      { to: '/admin/initialized-rolls', icon: Layers, label: 'Initialized (MM)' },
      { to: '/admin/bobbin-batches', icon: Package, label: 'Bobbin Batches' },
      { to: '/admin/dispatch', icon: Truck, label: 'Dispatch' },
    ],
  },
  {
    section: 'Intelligence',
    items: [
      { to: '/admin/reports', icon: BarChart2, label: 'Reports' },
      { to: '/admin/costing', icon: DollarSign, label: 'Costing Engine' },
      { to: '/admin/announcements', icon: Megaphone, label: 'Announcements' },
    ],
  },
]

const PAGE_TITLES = {
  '/admin': 'Dashboard',
  '/admin/notifications': 'Notifications',
  '/admin/units': 'Unit Management',
  '/admin/users': 'User Management',
  '/admin/parties': 'Parties / Customers',
  '/admin/vendors': 'Vendors',
  '/admin/procurement': 'Procurement',
  '/admin/cutting-logs': 'Cutting Records',
  '/admin/reports': 'Reports',
  '/admin/costing': 'Costing Engine',
  '/admin/machines': 'Machines & Positions',
  '/admin/operators': 'Operators',
  '/admin/shades': 'Shade Masters',
  '/admin/containers': 'Containers',
  '/admin/dispatch': 'Dispatch',
  '/admin/announcements': 'Announcements',
  '/admin/initialized-rolls': 'Initialized Rolls (Metallic Master Tracking)',
  '/admin/bobbin-batches': 'Bobbin Batches (Production)',
}

function SidebarContent({ onClose, unreadCount }) {
  const { user, logout } = useAuthStore()
  const navigate = useNavigate()

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  return (
    <div className="flex flex-col h-full bg-white border-r border-gray-100 w-56">
      <div className="px-5 py-4 border-b border-gray-100 flex-shrink-0">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 bg-brand-600 rounded-lg flex items-center justify-center text-white text-xs font-bold font-mono flex-shrink-0">
            JT
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-800">Jaritex</p>
            <p className="text-[10px] text-gray-400">Admin Panel</p>
          </div>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-4">
        {NAV_GROUPS.map(({ section, items }) => (
          <div key={section}>
            <p className="text-[9px] font-semibold text-gray-400 uppercase tracking-widest px-2 mb-1">
              {section}
            </p>
            {items.map(({ to, icon: Icon, label }) => {
              const badge = to === '/admin/notifications' ? unreadCount : null

              return (
                <NavLink
                  key={to}
                  to={to}
                  end={to === '/admin'}
                  onClick={onClose}
                  className={({ isActive }) =>
                    clsx(
                      'nav-item mb-0.5',
                      isActive ? 'nav-item-active' : 'nav-item-default'
                    )
                  }
                >
                  {({ isActive }) => (
                    <>
                      {isActive && (
                        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-4 bg-brand-600 rounded-r" />
                      )}
                      <Icon size={14} className="flex-shrink-0" />
                      <span>{label}</span>
                      {badge > 0 && (
                        <span className="ml-auto bg-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full leading-none">
                          {badge > 99 ? '99+' : badge}
                        </span>
                      )}
                    </>
                  )}
                </NavLink>
              )
            })}
          </div>
        ))}
      </nav>

      <div className="border-t border-gray-100 p-3 flex-shrink-0">
        <div className="flex items-center gap-2.5 px-2 py-2 rounded-lg hover:bg-gray-50 cursor-pointer">
          <div className="w-7 h-7 rounded-full bg-brand-600 flex items-center justify-center text-white text-[11px] font-semibold flex-shrink-0">
            {user?.name?.slice(0, 2).toUpperCase() || 'AD'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-gray-800 truncate">{user?.name || 'Admin'}</p>
            <p className="text-[10px] text-gray-400">Company Head</p>
          </div>
          <button
            onClick={handleLogout}
            className="text-gray-400 hover:text-red-500 transition-colors p-1"
            title="Logout"
          >
            <LogOut size={13} />
          </button>
        </div>
      </div>
    </div>
  )
}

export default function AdminLayout({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false)
  const location = useLocation()
  const pageTitle = PAGE_TITLES[location.pathname] || 'Admin'
  const { data } = useNotifications({ limit: 100, acknowledged: false })

  const unreadCount = data?.data?.length || 0

  return (
    <div className="flex h-screen overflow-hidden bg-surface-page">
      <div className="hidden md:flex md:flex-shrink-0">
        <SidebarContent onClose={() => { }} unreadCount={unreadCount} />
      </div>

      {mobileOpen && (
        <div className="fixed inset-0 z-50 flex md:hidden">
          <div
            className="fixed inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
          />
          <div className="relative z-10 flex flex-col w-56 bg-white h-full shadow-2xl">
            <button
              onClick={() => setMobileOpen(false)}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 z-10 p-1"
            >
              <X size={18} />
            </button>
            <SidebarContent onClose={() => setMobileOpen(false)} unreadCount={unreadCount} />
          </div>
        </div>
      )}

      <div className="flex flex-col flex-1 overflow-hidden min-w-0">
        <header className="h-14 bg-white border-b border-gray-100 flex items-center justify-between px-4 md:px-6 flex-shrink-0">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setMobileOpen(true)}
              className="md:hidden text-gray-500 hover:text-gray-800 p-1"
            >
              <Menu size={20} />
            </button>
            <h1 className="text-[15px] font-semibold text-gray-800">{pageTitle}</h1>
          </div>

          <div className="flex items-center gap-2">
            <NavLink
              to="/admin/notifications"
              className="relative w-8 h-8 rounded-lg bg-gray-50 border border-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-100 transition-colors"
            >
              <Bell size={15} />
              {unreadCount > 0 && (
                <>
                  <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 bg-red-500 rounded-full border border-white" />
                  <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center leading-none">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                </>
              )}
            </NavLink>
            <div className="md:hidden w-8 h-8 rounded-full bg-brand-600 flex items-center justify-center text-white text-xs font-semibold">
              AD
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-4 md:p-6">
          {children}
        </main>
      </div>
    </div>
  )
}
